// Application configuration for different environments
export interface AppConfig {
  api: {
    baseUrl: string
    userBaseUrl: string
    timeout: number
  }
  googleMaps: {
    apiKey: string
    libraries: ('places' | 'geometry')[]
    language: string
    region: string
    version: string
  }
  cognito: {
    region: string
    userPoolId: string
    userPoolWebClientId: string
    googleClientId?: string
    hostedUIDomain?: string
  }
  app: {
    name: string
    version: string
    environment: 'development' | 'production'
    defaultLanguage: string
    defaultCurrency: string
  }
  features: {
    enableGoogleMaps: boolean
    enablePWA: boolean
    enableAnalytics: boolean
    enableCognito: boolean
    enableCognitoGoogleOAuth: boolean
  }
  ui: {
    defaultPageSize: number
    maxPageSize: number
    animationDuration: number
    debounceDelay: number
  }
  checkout: {
    defaultShippingMethod: string
    supportedCities: string[]
    supportedPaymentMethods: string[]
  }
}

// Environment detection
const getEnvironment = (): 'development' | 'production' => {
  if (process.env.NODE_ENV === 'production') {
    return 'production'
  }
  return 'development'
}

const environment = getEnvironment()

// Environment-specific configurations
const configs: Record<string, AppConfig> = {
  development: {
    api: {
      baseUrl: process.env.REACT_APP_API_URL || 'https://devapi.seatersonline.com/api/ecom',
      userBaseUrl: process.env.REACT_APP_USER_API_URL || 'https://devapi.seatersonline.com/api',
      timeout: 10000,
    },
    googleMaps: {
      apiKey:
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBk9X64Qpv8BUQ79tVXUVdhnZmdwrGjOaE',
      libraries: ['places', 'geometry'],
      language: 'uk',
      region: 'UA',
      version: 'weekly',
    },
    cognito: {
      region: process.env.REACT_APP_COGNITO_REGION || 'eu-central-1',
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'eu-central-1_diDwRxvLr',
      userPoolWebClientId:
        process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID || '96rljbojj4vktdr7udjt28j36',
      googleClientId:
        process.env.REACT_APP_COGNITO_GOOGLE_CLIENT_ID ||
        '377871288942-l6rgt3q8irojbdqj6ljbol01gmgu64b4.apps.googleusercontent.com',
      hostedUIDomain: process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN || 'authdev.seaters.coffee',
    },
    app: {
      name: 'seaters',
      version: '1.0.0',
      environment: 'development',
      defaultLanguage: 'uk',
      defaultCurrency: 'UAH',
    },
    features: {
      enableGoogleMaps: true,
      enablePWA: false,
      enableAnalytics: false,
      enableCognito: true,
      enableCognitoGoogleOAuth: true,
    },
    ui: {
      defaultPageSize: 12,
      maxPageSize: 100,
      animationDuration: 300,
      debounceDelay: 500,
    },
    checkout: {
      defaultShippingMethod: 'Нова Пошта',
      supportedCities: ['Київ', 'Львів', 'Харків', 'Дніпро', 'Одеса'],
      supportedPaymentMethods: ['card', 'cash', 'bank_transfer'],
    },
  },
  production: {
    api: {
      baseUrl: process.env.REACT_APP_API_URL || 'https://devapi.seatersonline.com/api/ecom',
      userBaseUrl: process.env.REACT_APP_USER_API_URL || 'https://devapi.seatersonline.com/api',
      timeout: 20000,
    },
    googleMaps: {
      apiKey:
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBk9X64Qpv8BUQ79tVXUVdhnZmdwrGjOaE',
      libraries: ['places', 'geometry'],
      language: 'uk',
      region: 'UA',
      version: 'weekly',
    },
    cognito: {
      region: process.env.REACT_APP_COGNITO_REGION || 'eu-central-1',
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'eu-central-1_6pQo1LjFt',
      userPoolWebClientId:
        process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID || '5ac64nktp0ce2659iatlabt10b',
      googleClientId:
        process.env.REACT_APP_COGNITO_GOOGLE_CLIENT_ID ||
        '377871288942-8ja1nnoh3ujl0fg808bdlivr6h7fgkkv.apps.googleusercontent.com',
      hostedUIDomain: process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN || 'auth.seaters.coffee',
    },
    app: {
      name: 'seaters',
      version: '1.0.0',
      environment: 'production',
      defaultLanguage: 'uk',
      defaultCurrency: 'UAH',
    },
    features: {
      enableGoogleMaps: true,
      enablePWA: true,
      enableAnalytics: true,
      enableCognito: true,
      enableCognitoGoogleOAuth: true,
    },
    ui: {
      defaultPageSize: 12,
      maxPageSize: 100,
      animationDuration: 300,
      debounceDelay: 500,
    },
    checkout: {
      defaultShippingMethod: 'Нова Пошта',
      supportedCities: ['Київ', 'Львів', 'Харків', 'Дніпро', 'Одеса'],
      supportedPaymentMethods: ['card', 'cash', 'bank_transfer'],
    },
  },
}

// Export the current environment configuration
export const config: AppConfig = configs[environment]

// Export individual config sections for convenience
export const apiConfig = config.api
export const googleMapsConfig = config.googleMaps
export const cognitoConfig = config.cognito
export const appConfig = config.app
export const featuresConfig = config.features
export const uiConfig = config.ui
export const checkoutConfig = config.checkout

// Utility functions
export const isDevelopment = () => environment === 'development'
export const isProduction = () => environment === 'production'

// Feature flags
export const isFeatureEnabled = (feature: keyof AppConfig['features']): boolean => {
  return config.features[feature]
}

// Validation
export const validateConfig = (): string[] => {
  const errors: string[] = []

  if (!config.api.baseUrl) {
    errors.push('API base URL is required')
  }

  if (config.features.enableGoogleMaps && !config.googleMaps.apiKey) {
    errors.push('Google Maps API key is required when Google Maps is enabled')
  }

  if (config.features.enableCognito) {
    if (!config.cognito.region) {
      errors.push('Cognito region is required when Cognito is enabled')
    }
    if (!config.cognito.userPoolId) {
      errors.push('Cognito User Pool ID is required when Cognito is enabled')
    }
    if (!config.cognito.userPoolWebClientId) {
      errors.push('Cognito User Pool Web Client ID is required when Cognito is enabled')
    }
  }

  if (config.features.enableCognitoGoogleOAuth) {
    if (!config.cognito.googleClientId) {
      errors.push('Cognito Google Client ID is required when Cognito Google OAuth is enabled')
    }
  }

  return errors
}

